import { ObjectTools } from '../../../tools/object-tools';
import {
  SolDialog,
  SolDialogButton,
  SolDialogHeight,
  SolDialogType,
  SolDialogWidth,
} from '../../../models/core/sol-dialog';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolText } from '../../../models/core/sol-text';

export interface DialogButton {
  id: string;
  text: SolText;
  type: SolButtonType;
}

export class SolDialogViewState {
  private constructor(
    public readonly dialog: SolDialog | undefined,
    public readonly loading: boolean,
    public readonly hostClass: string,
    public readonly titleClass: string,
    public readonly chromeless: boolean,
    public readonly leftButtons: DialogButton[],
    public readonly rightButtons: DialogButton[],
    public readonly effectiveLoading: boolean,
    public readonly loadingActionId: string | undefined,
  ) {}

  public static of(): SolDialogViewState {
    return new SolDialogViewState(undefined, false, '', '', false, [], [], false, undefined);
  }

  public withDialog(dialog: SolDialog | undefined): SolDialogViewState {
    return this.copy({ dialog }).recalculate();
  }

  public withLoading(loading: boolean): SolDialogViewState {
    return this.copy({ loading }).recalculate();
  }

  private recalculate(): SolDialogViewState {
    const chromeless = this.dialog?.type === 'chromeless';
    const hostClass = SolDialogViewState.toHostClass(this.dialog, chromeless);
    const titleClass = SolDialogViewState.toTitleClass(this.dialog);
    const buttons = this.dialog?.buttons ?? [];
    const primaryButtonId = this.dialog?.primaryButtonId ?? '';
    const dialogType = this.dialog?.type ?? 'default';
    const leftButtons = buttons
      .filter(button => button.position === 'left')
      .map(button => SolDialogViewState.toButton(button, primaryButtonId, dialogType));
    const rightButtons = buttons
      .filter(button => button.position === 'right')
      .map(button => SolDialogViewState.toButton(button, primaryButtonId, dialogType));
    const effectiveLoading = (this.dialog?.loading ?? false) || this.loading;
    const loadingActionId = this.dialog?.loadingActionId;
    return this.copy({
      hostClass,
      titleClass,
      chromeless,
      leftButtons,
      rightButtons,
      effectiveLoading,
      loadingActionId,
    });
  }

  private static toHostClass(dialog: SolDialog | undefined, chromeless: boolean): string {
    const widthClass = SolDialogViewState.toWidthClass(dialog?.width ?? 'default');
    const heightClass = SolDialogViewState.toHeightClass(dialog?.height ?? 'default');
    const chromelessClass = chromeless ? 'sol-dialog-chromeless' : '';
    return ['sol-dialog', widthClass, heightClass, chromelessClass].filter(Boolean).join(' ');
  }

  private static toWidthClass(width: SolDialogWidth): string {
    switch (width) {
      case 'default':
        return '';
      case 'small':
        return 'sol-dialog-w-768';
      case 'medium':
        return 'sol-dialog-w-1280';
      case 'large':
        return 'sol-dialog-w-large';
    }
  }

  private static toHeightClass(height: SolDialogHeight): string {
    switch (height) {
      case 'default':
        return '';
      case 'small':
        return 'sol-dialog-h-512';
      case 'medium':
        return 'sol-dialog-h-896';
      case 'large':
        return 'sol-dialog-h-large';
    }
  }

  private static toTitleClass(dialog?: SolDialog): string {
    return dialog?.type === 'danger' ? 'sol-dialog-header-danger' : 'sol-dialog-header';
  }

  private static toButtonType(
    button: SolDialogButton,
    primaryButtonId: string,
    dialogType: SolDialogType,
  ): SolButtonType {
    if (button.id === primaryButtonId) {
      return dialogType === 'danger' ? SolButtonType.danger : SolButtonType.primary;
    }
    return SolButtonType.secondary;
  }

  private static toButton(button: SolDialogButton, primaryButtonId: string, dialogType: SolDialogType): DialogButton {
    return {
      id: button.id,
      text: button.text,
      type: SolDialogViewState.toButtonType(button, primaryButtonId, dialogType),
    };
  }

  private copy(props: Partial<SolDialogViewState>): SolDialogViewState {
    return ObjectTools.copy<SolDialogViewState>(this, props);
  }
}
